import styled from '@emotion/styled';
import React from 'react';
import { Helmet } from 'react-helmet';

import LinkButton, { BasicLinkButton } from '../../elements/LinkButton';
import withTracking from '../Common/withTracking';
import ChatWithUsButton from './ChatWithUsButton';

import { theme } from '@kindur/components';

import blackChevron from '../../icons/smartdraw/chevron-black.svg';
import whiteChevron from '../../icons/smartdraw/chevron-white.svg';
import logo from '../../images/logo.png';

const Container = styled('div')<{ visible: boolean }>`
  align-items: center;
  background-color: #f5f8f8;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.08);
  display: none;
  height: 96px;
  justify-content: space-between;
  padding: 0 16px 0 24px;
  position: fixed;
  top: ${({ visible }) => (visible ? 0 : '-96px')};
  transition: bottom 250ms, top 250ms;
  width: 100%;
  z-index: 10;
  ${theme.breakpoints.tabletDown} {
    display: flex;
    background-color: #e9e8e7;
    bottom: ${({ visible }) => (visible ? 0 : '-96px')};
    box-shadow: unset;
    padding: 0 12px;
    top: unset;
  }
`;
const LogoContainer = styled('div')`
  margin: ${theme.spacing.standard}px 0 ${theme.spacing.standard}px ${theme.spacing.standard}px;
  ${theme.breakpoints.tabletDown} {
    display: none;
  }
`;
const LogoLink = styled(LinkButton)`
  background: none;
  border: 0;
  margin: 0;
  padding: 0;
`;
const Logo = styled('img')`
  max-width: 64px;
  width: 64px;
`;
const ButtonsContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  min-width: 360px;
  ${theme.breakpoints.tabletDown} {
    min-width: 100%;
  }
`;
const CTAButton = styled(BasicLinkButton)`
  align-items: center;
  background-color: #0af7da;
  border-radius: 4px;
  box-shadow: -2px 2px 0 0 #0b4b40;
  color: #000;
  display: flex;
  flex-flow: row-reverse;
  font-family: 'Apercu Pro Bold', sans-serif;
  font-size: 18px;
  height: 48px;
  justify-content: space-between;
  letter-spacing: 0.09px;
  line-height: 24px;
  margin: 18px 24px 16px 0;
  padding: 0 24px;
  text-decoration: none;
  width: 200px;
  span {
    white-space: nowrap;
  }
  .white-chevron {
    display: none;
  }
  ${theme.breakpoints.tabletDown} {
    line-height: 48px;
    margin: 18px 0;
    padding: 0 12px;
    .black-chevron {
      display: inline-block;
    }
    .white-chevron {
      display: none;
    }
  }
`;

const hideZendeskWidget = `
  ${theme.breakpoints.tabletDown} {
    iframe#launcher {
      z-index: -10000 !important;
    }
  }
`;

const TrackComponentName = 'Toolbar';

export interface CTAProps {
  className: string;
  href: string;
  text: string;
}

export interface ToolbarProps {
  cta: CTAProps;
  track: (eventName: string, useComponentName: string) => void;
  location: Location;
  visible: boolean;
}

const Toolbar = ({ cta, location, track, visible }: ToolbarProps) => (
  <Container id={`toolbar`} visible={visible}>
    <Helmet>
      <style type={`text/css`}>{hideZendeskWidget}</style>
    </Helmet>
    <LogoContainer>
      <LogoLink
        to={'/'}
        title={'Kindur Homepage'}
        onClick={() => {
          track(`Logo`, TrackComponentName);
        }}
      >
        <Logo src={logo} alt='Kindur logo' />
      </LogoLink>
    </LogoContainer>
    <ButtonsContainer>
      <ChatWithUsButton
        location={location}
        track={track}
        trackEventParent={TrackComponentName}
        text={`Chat Now`}
      />
      <CTAButton
        className={cta.className || ''}
        href={cta.href}
        onClick={() => track(cta.text, TrackComponentName)}
      >
        <img src={blackChevron} className={`black-chevron`} alt={`chevron icon`} />
        <img src={whiteChevron} className={`white-chevron`} alt={`chevron icon`} />
        <span>{cta.text}</span>
      </CTAButton>
    </ButtonsContainer>
  </Container>
);

Toolbar.displayName = TrackComponentName;

export default withTracking(Toolbar);
